import { API_URL } from "src/scenes/App";
import axios from "axios";
import { UserKeys } from "src/constants/userDetails";
import {
    AUTOMATION_ITEM_TYPE_COLORS,
    AUTOMATION_ITEM_TYPES,
    EXTERNAL_CALENDAR_PROVIDERS,
    normaliseCalendarItemSource,
    normaliseCompanyUser,
    EVENT_SOURCE_LABEL,
    getTimezonePlaceName,
    SHARED_AUDIENCE,
} from "../calendarData";
import {
    CalendarItem,
    CalendarConnection,
    CalendarAttachment,
    AutomationSourceFeature,
    CalendarStatus,
    CalendarVisibility,
    CompanyUser,
    CompanyUsersStatus,
    CustomItemType,
    ExternalCalendarProvider,
    Holiday,
    LocationInfo,
    LocationSuggestion,
    ManagedItemType,
    RawCompanyUser,
    SharedAudience,
    ThemeMode,
    VisibilityRecipients,
    MeetingConfiguration,
    WeatherForecastDay
} from "../types";

const LOCATION_SEARCH_API_URL = "https://nominatim.openstreetmap.org/search";
const LOCATION_REVERSE_SEARCH_API_URL = "https://nominatim.openstreetmap.org/reverse";
const LOCATION_SEARCH_LIMIT = 5;
const WEATHER_API_URL = "https://api.open-meteo.com/v1/forecast";
const HOLIDAY_API_URL = "https://date.nager.at/api/v3/publicholidays";
const CALENDAR_API_PATH = "/calendar";
const CALENDAR_THEME_STORAGE_KEY = "dailyfixme.calendar.theme";
const CALENDAR_TIMEZONE_STORAGE_KEY = "dailyfixme.calendar.timezone";
const CALENDAR_PUBLIC_HOLIDAYS_STORAGE_KEY = "dailyfixme.calendar.showPublicHolidays";
const DEFAULT_CALENDAR_TIMEZONE = "Australia/Melbourne";

/** Map of subdivisions known for a given country code. Used for looking up public holidays. */
const LOCATION_SUBDIVISION_MAP: Record<string, Record<string, string>> = {
    AU: {
        VIC: "AU-VIC",
        Victoria: "AU-VIC",
        NSW: "AU-NSW",
        "New South Wales": "AU-NSW",
        QLD: "AU-QLD",
        Queensland: "AU-QLD",
        SA: "AU-SA",
        "South Australia": "AU-SA",
        WA: "AU-WA",
        "Western Australia": "AU-WA",
        TAS: "AU-TAS",
        Tasmania: "AU-TAS",
        NT: "AU-NT",
        "Northern Territory": "AU-NT",
        ACT: "AU-ACT",
        "Australian Capital Territory": "AU-ACT",
    },
};

const WEATHER_CODE_MAP: Record<number, { label: string; icon: string }> = {
    0: { label: "Clear", icon: "☀️" },
    1: { label: "Mostly clear", icon: "🌤️" },
    2: { label: "Partly cloudy", icon: "⛅" },
    3: { label: "Cloudy", icon: "☁️" },
    45: { label: "Fog", icon: "🌫️" },
    48: { label: "Fog", icon: "🌫️" },
    51: { label: "Drizzle", icon: "🌦️" },
    53: { label: "Drizzle", icon: "🌦️" },
    55: { label: "Drizzle", icon: "🌧️" },
    61: { label: "Rain", icon: "🌧️" },
    63: { label: "Rain", icon: "🌧️" },
    65: { label: "Heavy rain", icon: "⛈️" },
    71: { label: "Snow", icon: "🌨️" },
    73: { label: "Snow", icon: "🌨️" },
    75: { label: "Heavy snow", icon: "❄️" },
    80: { label: "Showers", icon: "🌦️" },
    81: { label: "Showers", icon: "🌦️" },
    82: { label: "Heavy showers", icon: "⛈️" },
    95: { label: "Thunderstorm", icon: "⛈️" },
    96: { label: "Storm", icon: "⛈️" },
    99: { label: "Storm", icon: "⛈️" },
};

const hasScheduledItemFields = (item: Partial<CalendarItem>): boolean => "startAt" in item && "endAt" in item && Boolean(item.startAt && item.endAt);

type LocationSearchResult = {
    place_id: string | number;
    display_name: string;
    lat?: string;
    lon?: string;
    address?: {
        road?: string;
        suburb?: string;
        city?: string;
        town?: string;
        village?: string;
        state?: string;
        country?: string;
        country_code?: string;
    };
};

type CalendarItemFilters = {
    from: string;
    to: string;
    audience?: SharedAudience;
    isPersonal?: boolean;
    sourceType?: "externalCalendar" | "automation";
    externalCalendarProvider?: ExternalCalendarProvider;
    automationFeature?: AutomationSourceFeature;
    itemTypeUuid?: string;
};

type CalendarSettings = {
    attendeeTimezones: string[];
    timezone: string;
    theme: ThemeMode;
    timezoneAttendees: string[];
};

type CalendarSettingsApiResponse = {
    attendeeTimezones: string[];
};

type CalendarSettingsWriteRequest = {
    attendeeTimezones?: string[];
};

type CalendarItemApiResponse = Partial<CalendarItem> & Partial<CalendarItem> & {
    itemUuid: string | null;
    id?: string;
    isPersonal: boolean;
    visibility?: CalendarVisibility;
    visibilityRecipients: CalendarItem["visibilityRecipients"] | null;
    sourceFeature?: AutomationSourceFeature | null;
    sourceDetail?: ApiCalendarSourceDetail | null;
};

type ApiCalendarSourceDetail = CalendarItem["automationDetail"] & {
    entityType?: string;
    entityUuid?: string;
};

type CalendarItemTypeApiResponse = {
    itemTypeUuid: string;
    key: string;
    label: string;
    color: string;
    isPersonal: boolean;
    builtIn: boolean;
};

type CalendarItemWriteRequest = {
    title: string;
    description?: string;
    isPersonal: boolean;
    visibilityRecipients?: VisibilityRecipients;
    color?: string;
    location?: string;
    url?: string;
    invites?: string;
    attachments: CalendarAttachment[];
    reminderOffset?: string;
    startAt?: string;
    endAt?: string;
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
    dueDate?: string;
    dueTime?: string;
    allDay?: boolean;
    completed?: boolean;
    progress?: number;
    itemTypeUuid?: string;
    priority?: CalendarItem["priority"];
    repeat?: CalendarItem["repeat"];
    recurring?: boolean;
    meetingUrl?: string;
    visibility?: CalendarItem["visibility"];
    meeting?: MeetingConfiguration;
};

type CalendarAttachmentUploadResponse = Pick<CalendarAttachment, "uuid"> & { url: string };

type ExternalCalendarConnectionApiResponse = {
    connectionUuid: string;
    provider: ExternalCalendarProvider;
    accountEmail: string | null;
    status: string;
    lastSyncedAt: string | null;
    lastError: string | null;
    calendars: Array<{
        calendarUuid: string;
        label: string;
        color: string | null;
        accessRole: string | null;
        selected: boolean;
    }>;
};

const toExternalCalendarConnection = (
    connection: ExternalCalendarConnectionApiResponse
): CalendarConnection => {
    const provider = EXTERNAL_CALENDAR_PROVIDERS.find(item => item.provider === connection.provider);
    const selectedCount = connection.calendars.filter(calendar => calendar.selected).length;
    return {
        id: connection.connectionUuid,
        label: provider?.label || EVENT_SOURCE_LABEL[connection.provider],
        sublabel: `${connection.accountEmail || "Connected account"} · ${selectedCount} selected`,
        source: connection.provider,
        connected: true,
        status: connection.status,
        color: provider?.color || "#64748b",
        accountEmail: connection.accountEmail,
        lastSyncedAt: connection.lastSyncedAt,
        lastError: connection.lastError,
        calendars: connection.calendars,
    };
};

class CalendarApiError extends Error {
    status: number;
    data: unknown;
    apiMessage: string;

    constructor(message: string, status: number, data: unknown, apiMessage = "") {
        super(message);
        this.name = "CalendarApiError";
        this.status = status;
        this.data = data;
        this.apiMessage = apiMessage;
    }
}

const getAuthToken = (): string => getStorage()?.getItem(UserKeys.TOKEN) || "";

const assertCalendarApiConfigured = (): void => {
    if (!API_URL) {
        throw new Error("Calendar API URL is not configured.");
    }
};

const getCalendarApiUrl = (path: string, params?: URLSearchParams): string => `${API_URL}${CALENDAR_API_PATH}${path}${params?.toString() ? `?${params.toString()}` : ""}`;

const getApiErrorBodyMessage = (data: unknown): string => {
    if (typeof data === "string") return data;
    if (!data || typeof data !== "object") return "";
    const errorData = data as { error?: unknown; message?: unknown };
    if (typeof errorData.error === "string" && errorData.error.trim()) return errorData.error;
    if (typeof errorData.message === "string" && errorData.message.trim()) return errorData.message;
    return "";
};

const parseErrorResponseBody = async (response: Response): Promise<unknown> => {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
        return await response.json().catch(() => ({}));
    }
    return await response.text().catch(() => "");
};

export const getCalendarApiErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof CalendarApiError) {
        return error.apiMessage || fallback;
    }

    if (
        typeof error === "object"
        && error !== null
        && "getApiErrorDetails" in error
        && typeof error.getApiErrorDetails === "function"
    ) {
        const details = error.getApiErrorDetails() as { message: string };
        return details.message;
    }

    const bodyMessage = getApiErrorBodyMessage((error as { data?: unknown })?.data);
    if (bodyMessage) return bodyMessage;

    if (error instanceof Error && error.message && error.message !== "Calendar request failed.") {
        return error.message;
    }

    return fallback;
};

const fetchCalendarApi = async <T,>(path: string, init: RequestInit = {}, params?: URLSearchParams): Promise<T> => {
    const token = getAuthToken();
    const response = await fetch(getCalendarApiUrl(path, params), {
        ...init,
        headers: {
            ...(init.body ? { "Content-Type": "application/json" } : {}),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(init.headers || {}),
        },
    });

    if (!response.ok) {
        const data = await parseErrorResponseBody(response);
        const apiMessage = getApiErrorBodyMessage(data);
        throw new CalendarApiError(apiMessage || "Calendar request failed.", response.status, data, apiMessage);
    }

    return await response.json() as T;
};

const toApiIsPersonal = (audience: SharedAudience | undefined): boolean => audience === SHARED_AUDIENCE.PERSONAL;

const toLocalAudience = (item: { audience?: SharedAudience; isPersonal: boolean; visibilityRecipients: VisibilityRecipients | null }): SharedAudience => {
    if (item.audience) {
        return item.audience;
    }
    if (item.isPersonal || !item.visibilityRecipients) {
        return SHARED_AUDIENCE.PERSONAL;
    }
    const anyEmployees = item.visibilityRecipients.employees.all || item.visibilityRecipients.employees.selectedEmployeeUuids.length > 0;
    const anyClients = item.visibilityRecipients.clients.all || item.visibilityRecipients.clients.selectedEmployeeUuids.length > 0;
    if (anyEmployees && anyClients) {
        return SHARED_AUDIENCE.BOTH;
    }
    return anyEmployees ?
        SHARED_AUDIENCE.EMPLOYEES :
        SHARED_AUDIENCE.CLIENTS;
};

const toLocalAutomationSource = (item: CalendarItemApiResponse): CalendarItem["source"] | undefined => {
    if (item.source) return item.source;
    if (!item.sourceFeature) return undefined;
    return normaliseCalendarItemSource({
        automation: {
            feature: item.sourceFeature,
            label: EVENT_SOURCE_LABEL[item.sourceFeature] || item.sourceFeature,
            entityType: item.sourceDetail?.entityType,
            entityUuid: item.sourceDetail?.entityUuid,
            sourceReferenceId: item.sourceDetail?.entityUuid,
        },
    });
};

const toLocalAutomationTaskType = (sourceFeature: AutomationSourceFeature | null | undefined): CalendarItem["taskType"] | "" => {
    if (sourceFeature === "noticeboard") return AUTOMATION_ITEM_TYPES.NOTICEBOARD;
    if (sourceFeature === "emailCampaign") return AUTOMATION_ITEM_TYPES.EMAIL_CAMPAIGN;
    if (sourceFeature === "goal" || sourceFeature === "teamGoal") return AUTOMATION_ITEM_TYPES.GOAL_TRACKING;
    if (sourceFeature === "habit") return AUTOMATION_ITEM_TYPES.HABIT;
    if (sourceFeature === "mood") return AUTOMATION_ITEM_TYPES.MOOD;
    if (sourceFeature === "checklist") return AUTOMATION_ITEM_TYPES.CHECKLIST;
    if (sourceFeature === "survey") return AUTOMATION_ITEM_TYPES.SURVEY;
    if (sourceFeature === "poll") return AUTOMATION_ITEM_TYPES.POLL;
    return "";
};

const toLocalAutomationFeature = (sourceFeature: AutomationSourceFeature | null | undefined): string => {
    return sourceFeature || "";
};

const toLocalAutomationDetail = (item: CalendarItemApiResponse): CalendarItem["automationDetail"] | undefined => {
    const automationDetail = item.automationDetail || item.sourceDetail;
    const feature = toLocalAutomationFeature(item.sourceFeature);
    if (!automationDetail && !feature) return undefined;
    const metadata = automationDetail?.metadata && typeof automationDetail.metadata === "object"
        ? automationDetail.metadata
        : {};

    return {
        ...metadata,
        ...(automationDetail || {}),
        feature: feature || automationDetail?.feature,
        sourceReferenceId: automationDetail?.sourceReferenceId || item.sourceDetail?.entityUuid,
    };
};

const toApiAttachments = (attachments: CalendarItem["attachments"] | undefined): CalendarAttachment[] => attachments || [];

const getCalendarAttachmentExtension = (file: File): string => {
    const extension = file.name.split(".").pop();
    if (!extension || extension === file.name) {
        throw new Error("Attachment files must have an extension.");
    }
    return extension.toLowerCase();
};

const getStoredAttachmentUrl = (uploadUrl: string): string => {
    const parsedUrl = new URL(uploadUrl);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
};

export const uploadCalendarAttachment = async (file: File): Promise<CalendarAttachment> => {
    assertCalendarApiConfigured();
    const token = getAuthToken();
    const extension = getCalendarAttachmentExtension(file);
    const uploadUrlResponse = await axios.get<CalendarAttachmentUploadResponse>(
        getCalendarApiUrl(`/uploadUrl/${encodeURIComponent(extension)}`),
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    const { uuid, url } = uploadUrlResponse.data;
    await axios.put(url, file, { headers: { "Content-Type": file.type || "application/octet-stream" } });

    return {
        uuid,
        s3Url: getStoredAttachmentUrl(url),
        filename: file.name,
    };
};

const getLocalCalendarItemId = (item: CalendarItemApiResponse): string => (
    item.id
    || item.itemUuid
    || item.sourceDetail?.entityUuid
    || item.sourceDetail?.sourceReferenceId
    || `${item.sourceFeature || "calendar"}-${item.title || "item"}-${item.startAt || item.startDate || item.dueDate || item.createdAt || ""}`
);

const toLocalCalendarStatus = (status: string | undefined, completed?: boolean): CalendarStatus => {
    if (status === "done" || completed) return "done";
    return "active";
};

const withLocalItemId = <T extends CalendarItemApiResponse>(item: T): CalendarItem => {
    const automationTaskType = toLocalAutomationTaskType(item.sourceFeature);
    const actionItem = item as Partial<CalendarItem>;
    const itemColor = item.color;
    const localItem = {
        ...item,
        id: getLocalCalendarItemId(item),
        itemUuid: item.itemUuid || undefined,
        audience: toLocalAudience(item),
        visibility: item.visibility || (item.isPersonal ? "personal" : "shared"),
        status: toLocalCalendarStatus(item.status, actionItem.completed),
        visibilityRecipients: item.visibilityRecipients || null,
        source: toLocalAutomationSource(item),
        automationDetail: toLocalAutomationDetail(item),
    } as CalendarItem;

    if (!hasScheduledItemFields(item) && automationTaskType) {
        return {
            ...localItem,
            taskType: actionItem.taskType || automationTaskType,
            color: itemColor || AUTOMATION_ITEM_TYPE_COLORS[automationTaskType],
        } as CalendarItem;
    }

    return localItem;
};

const toCalendarItemWriteRequest = (item: CalendarItem): CalendarItemWriteRequest => {
    const meeting = item.meeting
        ? "enabled" in item.meeting
            ? item.meeting
            : {
                enabled: true,
                timezone: item.meeting.timezone,
                internalParticipantUuids: item.meeting.internalParticipantUuids,
                guestEmails: item.meeting.guestEmails,
                passwordEnabled: item.meeting.passwordEnabled,
                password: "",
                recurrenceEndsAt: item.meeting.recurrenceEndsAt,
            }
        : undefined;
    const request: CalendarItemWriteRequest = {
        title: item.title,
        description: item.description,
        isPersonal: toApiIsPersonal(item.audience),
        color: item.color,
        location: item.location,
        url: item.url,
        invites: item.invites,
        attachments: toApiAttachments(item.attachments),
        reminderOffset: item.reminderOffset,
        allDay: item.allDay,
        meetingUrl: item.meetingUrl,
        meeting,
    };
    if (!request.isPersonal) {
        request.visibilityRecipients = item.visibilityRecipients || undefined;
    }

    if (hasScheduledItemFields(item)) {
        return {
            ...request,
            startAt: item.startAt,
            endAt: item.endAt,
            visibility: item.visibility,
            itemTypeUuid: item.itemTypeUuid || undefined,
            repeat: item.repeat,
        };
    }

    return {
        ...request,
        startDate: item.startDate,
        startTime: item.startTime,
        endDate: item.endDate,
        endTime: item.endTime,
        dueDate: item.dueDate,
        dueTime: item.dueTime,
        completed: item.completed,
        progress: item.progress,
        itemTypeUuid: item.itemTypeUuid || undefined,
        priority: item.priority,
        repeat: item.repeat,
        recurring: item.recurring,
    };
};

const getStorage = (): Storage | null => {
    if (typeof window !== "undefined" && window.localStorage) return window.localStorage;
    if (typeof localStorage !== "undefined") return localStorage;
    return null;
};

export const getCalendarItems = async (filters: CalendarItemFilters): Promise<CalendarItem[]> => {
    assertCalendarApiConfigured();
    if (!filters.from || !filters.to) {
        throw new Error("Calendar item date range is required.");
    }

    const params = new URLSearchParams();
    params.set("from", filters.from);
    params.set("to", filters.to);
    if (typeof filters.isPersonal === "boolean") params.set("isPersonal", String(filters.isPersonal));
    if (!params.has("isPersonal") && filters.audience) params.set("isPersonal", String(filters.audience === SHARED_AUDIENCE.PERSONAL));
    if (filters.itemTypeUuid) params.set("itemTypeUuid", filters.itemTypeUuid);

    const items = await fetchCalendarApi<CalendarItemApiResponse[]>("/items", {}, params);
    return items.map(withLocalItemId);
};

export const getExternalCalendarConnections = async (): Promise<CalendarConnection[]> => {
    assertCalendarApiConfigured();
    const token = getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const connectionResponse = await axios.get<ExternalCalendarConnectionApiResponse[]>(
        getCalendarApiUrl("/external-calendar/connections"),
        { headers }
    );

    return connectionResponse.data.map(toExternalCalendarConnection);
};

export const startExternalCalendarAuthorisation = async (
    provider: ExternalCalendarProvider
): Promise<string> => {
    assertCalendarApiConfigured();
    const token = getAuthToken();
    const response = await axios.post<{ authorizationUrl: string }>(
        getCalendarApiUrl(`/external-calendar/connections/${provider}/authorisation`),
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    return response.data.authorizationUrl;
};

export const completeExternalCalendarAuthorisation = async (
    state: string,
    code: string
): Promise<CalendarConnection> => {
    assertCalendarApiConfigured();
    const token = getAuthToken();
    const response = await axios.post<ExternalCalendarConnectionApiResponse>(
        getCalendarApiUrl("/external-calendar/connections/callback"),
        { state, code },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    return toExternalCalendarConnection(response.data);
};

export const connectAppleExternalCalendar = async (
    email: string,
    appSpecificPassword: string
): Promise<CalendarConnection> => {
    assertCalendarApiConfigured();
    const token = getAuthToken();
    const response = await axios.post<ExternalCalendarConnectionApiResponse>(
        getCalendarApiUrl("/external-calendar/connections/apple"),
        { email, appSpecificPassword },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    return toExternalCalendarConnection(response.data);
};

export const updateExternalCalendarSelection = async (
    connectionUuid: string,
    calendarUuids: string[]
): Promise<void> => {
    assertCalendarApiConfigured();
    const token = getAuthToken();
    await axios.put(
        getCalendarApiUrl(`/external-calendar/connections/${connectionUuid}/calendars`),
        { calendarUuids },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
};

export const syncExternalCalendarConnection = async (connectionUuid: string): Promise<void> => {
    assertCalendarApiConfigured();
    const token = getAuthToken();
    await axios.post(
        getCalendarApiUrl(`/external-calendar/connections/${connectionUuid}/sync`),
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
};

export const deleteExternalCalendarConnection = async (connectionUuid: string): Promise<void> => {
    assertCalendarApiConfigured();
    const token = getAuthToken();
    await axios.delete(
        getCalendarApiUrl(`/external-calendar/connections/${connectionUuid}`),
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
};

export const createCalendarItem = async <T extends CalendarItem>(item: T): Promise<T> => {
    assertCalendarApiConfigured();
    return withLocalItemId(await fetchCalendarApi<CalendarItemApiResponse>("/items", {
        method: "POST",
        body: JSON.stringify(toCalendarItemWriteRequest(item)),
    })) as T;
};

export const updateCalendarItem = async <T extends CalendarItem>(itemId: string, item: T): Promise<T> => {
    assertCalendarApiConfigured();
    return withLocalItemId(await fetchCalendarApi<CalendarItemApiResponse>(`/items/${encodeURIComponent(item.itemUuid || itemId)}`, {
        method: "PUT",
        body: JSON.stringify(toCalendarItemWriteRequest(item)),
    })) as T;
};

export const deleteCalendarItem = async (itemId: string): Promise<{ id: string; itemUuid: string }> => {
    assertCalendarApiConfigured();
    const deletedItem = await fetchCalendarApi<{ itemUuid: string }>(`/items/${encodeURIComponent(itemId)}`, { method: "DELETE" });
    return { id: deletedItem.itemUuid, itemUuid: deletedItem.itemUuid };
};

export const getCalendarTheme = (): ThemeMode => {
    const storedTheme = getStorage()?.getItem(CALENDAR_THEME_STORAGE_KEY);
    return storedTheme === "dark" || storedTheme === "light" ? storedTheme : "light";
};
export const getCalendarSettings = async (): Promise<CalendarSettings> => {
    assertCalendarApiConfigured();

    const settings = await fetchCalendarApi<CalendarSettingsApiResponse>("/settings");
    const attendeeTimezones = settings.attendeeTimezones || [];
    return {
        attendeeTimezones,
        timezone: getCalendarTimezone(),
        theme: getCalendarTheme(),
        timezoneAttendees: attendeeTimezones,
    };
};

export const updateCalendarSettings = async (settings: Partial<CalendarSettings>): Promise<CalendarSettings> => {
    assertCalendarApiConfigured();
    const updatedSettings = await fetchCalendarApi<CalendarSettingsApiResponse>("/settings", {
        method: "PUT",
        body: JSON.stringify({
            attendeeTimezones: settings.attendeeTimezones || settings.timezoneAttendees,
        } satisfies CalendarSettingsWriteRequest),
    });
    const attendeeTimezones = updatedSettings.attendeeTimezones || [];
    return {
        attendeeTimezones,
        timezone: getCalendarTimezone(),
        theme: getCalendarTheme(),
        timezoneAttendees: attendeeTimezones,
    };
};

export const saveCalendarTheme = (theme: ThemeMode): void => {
    getStorage()?.setItem(CALENDAR_THEME_STORAGE_KEY, theme);
};

export const getCalendarTimezone = (): string => getStorage()?.getItem(CALENDAR_TIMEZONE_STORAGE_KEY) || DEFAULT_CALENDAR_TIMEZONE;
export const hasStoredCalendarTimezone = (): boolean => Boolean(getStorage()?.getItem(CALENDAR_TIMEZONE_STORAGE_KEY));
export const saveCalendarTimezone = (timezone: string): void => {
    getStorage()?.setItem(CALENDAR_TIMEZONE_STORAGE_KEY, timezone);
};

export const getCalendarPublicHolidayVisibility = (): boolean => getStorage()?.getItem(CALENDAR_PUBLIC_HOLIDAYS_STORAGE_KEY) !== "false";
export const saveCalendarPublicHolidayVisibility = (showPublicHolidays: boolean): void => {
    getStorage()?.setItem(CALENDAR_PUBLIC_HOLIDAYS_STORAGE_KEY, String(showPublicHolidays));
};

export const fetchCalendarItemTypes = async (): Promise<ManagedItemType[]> => {
    assertCalendarApiConfigured();
    const itemTypes = await fetchCalendarApi<CalendarItemTypeApiResponse[]>("/item-types");
    return itemTypes
        .map(itemType => ({
            id: itemType.builtIn ? itemType.key : itemType.itemTypeUuid,
            itemTypeUuid: itemType.itemTypeUuid,
            label: itemType.label,
            color: itemType.color,
            scope: (itemType.builtIn ? "builtIn" : itemType.isPersonal ? "personal" : "company") as ManagedItemType["scope"],
        }));
};

export const createCalendarItemType = async (itemType: CustomItemType): Promise<CustomItemType> => {
    assertCalendarApiConfigured();
    const createdItemType = await fetchCalendarApi<CalendarItemTypeApiResponse>("/item-types", {
        method: "POST",
        body: JSON.stringify({
            label: itemType.label,
            color: itemType.color,
            isPersonal: itemType.scope === "personal",
        }),
    });

    return {
        id: createdItemType.itemTypeUuid,
        itemTypeUuid: createdItemType.itemTypeUuid,
        label: createdItemType.label,
        color: createdItemType.color,
        scope: createdItemType.isPersonal ? "personal" : "company",
    };
};

export const updateCalendarItemType = async (itemType: CustomItemType): Promise<CustomItemType> => {
    assertCalendarApiConfigured();
    const updatedItemType = await fetchCalendarApi<CalendarItemTypeApiResponse>(`/item-types/${encodeURIComponent(itemType.itemTypeUuid)}`, {
        method: "PUT",
        body: JSON.stringify({
            label: itemType.label,
            color: itemType.color,
            isPersonal: itemType.scope === "personal",
        }),
    });

    return {
        id: updatedItemType.itemTypeUuid,
        itemTypeUuid: updatedItemType.itemTypeUuid,
        label: updatedItemType.label,
        color: updatedItemType.color,
        scope: updatedItemType.isPersonal ? "personal" : "company",
    };
};

export const deleteCalendarItemType = async (itemTypeUuid: string): Promise<{ itemTypeUuid: string }> => {
    assertCalendarApiConfigured();
    return await fetchCalendarApi<{ itemTypeUuid: string }>(`/item-types/${encodeURIComponent(itemTypeUuid)}`, { method: "DELETE" });
};

export const getCalendarCompanyUsers = async (
    token: string,
    signal?: AbortSignal
): Promise<{ users: CompanyUser[]; status: CompanyUsersStatus }> => {
    try {
        const response = await fetch(`${API_URL}/survey/company/getAllUsers`, {
            headers: { Authorization: `Bearer ${token || getStorage()?.getItem(UserKeys.TOKEN) || ""}` },
            signal,
        });

        if (!response.ok) {
            const data = await parseErrorResponseBody(response);
            const apiMessage = getApiErrorBodyMessage(data);
            throw new CalendarApiError(apiMessage || "Failed to load company users.", response.status, data, apiMessage);
        }

        const data = await response.json() as { companyEmployees?: RawCompanyUser[] };
        const users = (data.companyEmployees || [])
            .map(normaliseCompanyUser)
            .filter(companyUser => companyUser.uuid);

        return users.length > 0
            ? { users, status: "ready" }
            : { users: [], status: "ready" };
    } catch (error) {
        if (signal?.aborted || (error as Error).name === "AbortError") {
            throw error;
        }
        console.error(error);
        return { users: [], status: "error" };
    }
};

export const getFallbackCalendarLocation = (): LocationInfo => ({
    city: "Unknown location",
    region: "",
    country: "",
    countryCode: "",
    latitude: null,
    longitude: null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

const getBrowserCoordinates = (): Promise<GeolocationCoordinates> => new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
        reject(new Error("Browser geolocation is unavailable."));
        return;
    }

    navigator.geolocation.getCurrentPosition(
        position => resolve(position.coords),
        reject,
        { enableHighAccuracy: false, maximumAge: 300000, timeout: 10000 }
    );
});

export const getDetectedCalendarLocation = async (): Promise<LocationInfo> => {
    const coordinates = await getBrowserCoordinates();
    const params = new URLSearchParams({
        lat: String(coordinates.latitude),
        lon: String(coordinates.longitude),
        format: "jsonv2",
        addressdetails: "1",
    });
    const response = await fetch(`${LOCATION_REVERSE_SEARCH_API_URL}?${params.toString()}`);
    if (!response.ok) {
        throw new Error("Failed to infer location.");
    }

    const data = await response.json() as LocationSearchResult;
    return {
        city: data.address?.city || data.address?.town || data.address?.village || data.address?.suburb || "Unknown location",
        region: data.address?.state || "",
        country: data.address?.country || "",
        countryCode: data.address?.country_code?.toUpperCase() || "",
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
};

export const getDetectedLocationLabel = (locationInfo: LocationInfo | null): string => {
    if (!locationInfo || locationInfo.city === "Unknown location") return "";
    return [locationInfo.city, locationInfo.region || locationInfo.countryCode || locationInfo.country]
        .filter(Boolean)
        .join(", ");
};

export const searchCalendarLocations = async (query: string, signal?: AbortSignal): Promise<LocationSuggestion[]> => {
    const params = new URLSearchParams({
        q: query,
        format: "jsonv2",
        addressdetails: "1",
        limit: String(LOCATION_SEARCH_LIMIT),
    });

    const response = await fetch(`${LOCATION_SEARCH_API_URL}?${params.toString()}`, { signal });
    if (!response.ok) {
        throw new Error("Failed to load location suggestions.");
    }

    const results = await response.json() as LocationSearchResult[];
    return (results || []).map((entry) => ({
        id: entry.place_id,
        label: entry.display_name,
        detail: [
            entry.address?.road,
            entry.address?.suburb || entry.address?.city || entry.address?.town || entry.address?.village,
            entry.address?.state,
            entry.address?.country,
        ].filter(Boolean).join(" · "),
    }));
};

export const getCalendarWeatherLocationForTimezone = async (timezone: string): Promise<LocationInfo> => {
    const placeName = getTimezonePlaceName(timezone);
    const fallbackLocation = {
        city: placeName,
        region: "",
        country: "",
        countryCode: "",
        latitude: null,
        longitude: null,
        timezone,
    };
    if (timezone === "UTC") {
        return fallbackLocation;
    }

    const params = new URLSearchParams({
        q: placeName,
        format: "jsonv2",
        addressdetails: "1",
        limit: "1",
    });
    const response = await fetch(`${LOCATION_SEARCH_API_URL}?${params.toString()}`);
    if (!response.ok) {
        throw new Error("Failed to resolve timezone weather location.");
    }

    const results = await response.json() as LocationSearchResult[];
    const result = results[0];
    if (!result) {
        return fallbackLocation;
    }

    return {
        city: result.address?.city || result.address?.town || result.address?.village || placeName,
        region: result.address?.state || "",
        country: result.address?.country || "",
        countryCode: result.address?.country_code?.toUpperCase() || "",
        latitude: Number(result.lat),
        longitude: Number(result.lon),
        timezone,
    };
};

const getWeatherPresentation = (code?: number): { label: string; icon: string } => (
    typeof code === "number" ? WEATHER_CODE_MAP[code] : null
) || { label: "Weather", icon: "🌤️" };

const formatForecastDay = (date: string, timezone: string): string => new Intl.DateTimeFormat("en-AU", {
    timeZone: timezone,
    weekday: "short",
}).format(new Date(`${date}T12:00:00`));

export const getCalendarWeatherForecast = async (locationInfo: LocationInfo, timezone: string): Promise<WeatherForecastDay[]> => {
    if (!Number.isFinite(locationInfo.latitude) || !Number.isFinite(locationInfo.longitude)) {
        return [];
    }

    const params = new URLSearchParams({
        latitude: String(locationInfo.latitude),
        longitude: String(locationInfo.longitude),
        daily: "weather_code,temperature_2m_max",
        forecast_days: "5",
        timezone,
    });
    const response = await fetch(`${WEATHER_API_URL}?${params.toString()}`);
    if (!response.ok) {
        throw new Error("Failed to load weather.");
    }

    const data = await response.json();
    const daily = data.daily || {};
    return (daily.time || []).map((date: string, index: number) => {
        const weather = getWeatherPresentation(daily.weather_code?.[index]);
        return {
            label: index === 0 ? "Today" : formatForecastDay(date, data.timezone || timezone),
            icon: weather.icon,
            weatherLabel: weather.label,
            max: Math.round(daily.temperature_2m_max?.[index]),
        };
    });
};

export const getLocationSubdivisionCode = (locationInfo: LocationInfo | null): string => {
    if (!locationInfo?.countryCode || !locationInfo?.region) return "";
    const countryCode = locationInfo.countryCode.toUpperCase();
    const regionMap = LOCATION_SUBDIVISION_MAP[countryCode];
    if (!regionMap) return "";
    return regionMap[locationInfo.region] || regionMap[locationInfo.region.toUpperCase()] || "";
};

const isHolidayRelevantForLocation = (holiday: Holiday | null | undefined, subdivisionCode: string): boolean => {
    if (!holiday) return false;
    if (holiday.global) return true;
    if (!Array.isArray(holiday.counties) || holiday.counties.length === 0) return true;
    return Boolean(subdivisionCode) && holiday.counties.includes(subdivisionCode);
};

const buildHolidayMap = (holidaySets: Holiday[][], subdivisionCode: string): Record<string, string[]> => holidaySets
    .flat()
    .reduce((acc, holiday) => {
        if (!holiday?.date || !isHolidayRelevantForLocation(holiday, subdivisionCode)) {
            return acc;
        }
        const label = holiday.localName || holiday.name;
        if (!label) return acc;
        if (!acc[holiday.date]) {
            acc[holiday.date] = [];
        }
        if (!acc[holiday.date].includes(label)) {
            acc[holiday.date].push(label);
        }
        return acc;
    }, {} as Record<string, string[]>);

export const getCalendarPublicHolidayMap = async ({
    countryCode,
    subdivisionCode,
    years,
}: {
    countryCode: string;
    subdivisionCode: string;
    years: number[];
}): Promise<Record<string, string[]>> => {
    const holidaySets = await Promise.all(
        years.map((year) => fetch(`${HOLIDAY_API_URL}/${year}/${countryCode.toUpperCase()}`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to load public holidays.");
                }
                return response.json() as Promise<Holiday[]>;
            })
            .catch((error): Holiday[] => {
                console.error(error);
                return [];
            }))
    );

    return buildHolidayMap(holidaySets, subdivisionCode);
};
